import * as XLSX from "xlsx";
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import { formatExportCell } from "./exportReportPdfFromExcelBlob";
import {
  CONTENT_W,
  MARGIN,
  X0,
  buildColumnStyles,
  drawLedgerSignatureFooter,
  drawLedgerStatementHeader,
  formatQtyCell,
  ledgerTableBaseStyles,
  loadLedgerLogos,
  sumNumeric,
} from "./ledgerStatementPdfShared";

const PALLET_COL_RATIOS = [0.11, 0.31, 0.06, 0.13, 0.13, 0.13, 0.13];

function parseStorageVisibilityExcelRows(rows) {
  const groupRow = Array.isArray(rows[0]) ? rows[0] : [];
  const bodyRows = rows.slice(2);
  const casesIndex = groupRow.findIndex(
    (cell) => String(cell).trim().toUpperCase() === "CASES"
  );
  const baseCount = casesIndex >= 0 ? casesIndex : 9;
  const casesStart = baseCount;

  return bodyRows
    .map((row) => {
      const cells = Array.isArray(row) ? row : [];
      return {
        skuCode: formatExportCell(cells[1] ?? ""),
        productDescription: formatExportCell(cells[2] ?? ""),
        uom: formatExportCell(cells[3] ?? ""),
        openingStock: cells[casesStart] ?? "",
        inwardQty: cells[casesStart + 1] ?? "",
        outwardQty: cells[casesStart + 2] ?? "",
        closingStock: cells[casesStart + 3] ?? "",
      };
    })
    .filter((r) =>
      [r.skuCode, r.productDescription, r.openingStock, r.inwardQty, r.outwardQty, r.closingStock].some(
        (v) => v != null && String(v).trim() !== ""
      )
    );
}

function consolidateLedgerRowsForPdf(rows) {
  const seen = new Set();
  const out = [];
  for (const row of rows) {
    const key = [
      row.skuCode,
      row.productDescription,
      row.uom,
      formatQtyCell(row.openingStock),
      formatQtyCell(row.inwardQty),
      formatQtyCell(row.outwardQty),
      formatQtyCell(row.closingStock),
    ].join("\x1f");
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(row);
  }
  return out;
}

export async function exportPalletStorageReportPdfFromExcelBlob(
  excelBlob,
  filename,
  context = {}
) {
  const data = await excelBlob.arrayBuffer();
  const workbook = XLSX.read(data, { type: "array", cellDates: true });
  const sheetName = workbook.SheetNames[0];
  if (!sheetName) {
    throw new Error("Export file has no worksheet");
  }

  const worksheet = workbook.Sheets[sheetName];
  const rows = XLSX.utils.sheet_to_json(worksheet, {
    header: 1,
    defval: "",
    raw: false,
  });

  const ledgerRows = consolidateLedgerRowsForPdf(parseStorageVisibilityExcelRows(rows));

  const totalRow = [
    "Total",
    "",
    "",
    formatQtyCell(sumNumeric(ledgerRows.map((r) => r.openingStock))),
    formatQtyCell(sumNumeric(ledgerRows.map((r) => r.inwardQty))),
    formatQtyCell(sumNumeric(ledgerRows.map((r) => r.outwardQty))),
    formatQtyCell(sumNumeric(ledgerRows.map((r) => r.closingStock))),
  ];

  const skuRows = ledgerRows.map((row) => [
    row.skuCode,
    row.productDescription,
    row.uom,
    formatQtyCell(row.openingStock),
    formatQtyCell(row.inwardQty),
    formatQtyCell(row.outwardQty),
    formatQtyCell(row.closingStock),
  ]);

  const logos = await loadLedgerLogos();

  const doc = new jsPDF({
    orientation: "landscape",
    unit: "pt",
    format: "a4",
  });

  const tableStartY = drawLedgerStatementHeader(doc, context, logos, {
    title: "PALLET LEDGER STATEMENT",
    clientHeading: "Client Name & Address",
  });

  const columnStyles = buildColumnStyles(PALLET_COL_RATIOS);
  columnStyles[3].halign = "right";
  columnStyles[4].halign = "right";
  columnStyles[5].halign = "right";
  columnStyles[6].halign = "right";

  autoTable(doc, {
    startY: tableStartY,
    tableWidth: CONTENT_W,
    margin: { left: X0, right: X0, bottom: MARGIN + 84 },
    head: [
      [
        { content: "SKU Code", styles: { valign: "middle" } },
        { content: "Product\nDescription", styles: { valign: "middle" } },
        { content: "UOM", styles: { valign: "middle" } },
        { content: "Opening\nStock", styles: { valign: "middle" } },
        { content: "Inward\nQty", styles: { valign: "middle" } },
        { content: "Outward\nQty", styles: { valign: "middle" } },
        { content: "Closing\nStock", styles: { valign: "middle" } },
      ],
    ],
    body: [totalRow, ...skuRows],
    ...ledgerTableBaseStyles,
    styles: { ...ledgerTableBaseStyles.styles, fontSize: 8, cellPadding: 3 },
    columnStyles,
    showHead: "everyPage",
    rowPageBreak: "avoid",
    didParseCell: (data) => {
      if (data.section === "body" && data.row.index === 0) {
        data.cell.styles.fontStyle = "bold";
        if (data.column.index >= 3) {
          data.cell.styles.halign = "right";
        }
      }
    },
  });

  drawLedgerSignatureFooter(doc, context, doc.lastAutoTable.finalY);

  doc.save(filename);
}
